import React, { useCallback } from 'react';
import { Box, Preloader } from '@common-fe/common-fe';

import ModalWrapper from '@/components/wrappers/ModalWrapper';

import { ManageAccountAccessStep, useManageAccountAccessState } from './hooks/useManageAccountAccessState';
import { useGetCardPurses } from './queries/useGetCardPurses.query';
import { useGetLockReasons } from './queries/useGetLockReasons.query';
import { usePatchCardPurses } from './queries/usePatchCardPurses.query';
import { ConfirmAccountLock, ManageAccountAccessForm } from './components';

interface Props {
  employeeId?: string;
  visible: boolean;
  onCancel: () => void;
  onSuccess?: () => void;
}

export const ManageAccountAccessModal: React.FC<Props> = ({ employeeId, visible, onCancel, onSuccess }) => {
  const { purses, isLoading } = useGetCardPurses(employeeId, visible);
  const { lockReasonOptions } = useGetLockReasons();
  const { patchCardPurses, isPending } = usePatchCardPurses();
  const {
    step,
    lockedIds,
    editableLockedIds,
    employeeUnlockForbiddenIds,
    isAllEmployeeUnlockForbidden,
    lockReasons,
    pursesRequiringLockReason,
    hasChanges,
    toggleLock,
    setEmployeeUnlockForbidden,
    setAllEmployeeUnlockForbidden,
    setLockReason,
    goToConfirm,
    goToForm,
    reset,
    buildPayload,
  } = useManageAccountAccessState({ purses });

  const handleCancel = useCallback(() => {
    reset();
    onCancel();
  }, [reset, onCancel]);

  const handleSubmit = useCallback(async () => {
    if (hasChanges) {
      await patchCardPurses({
        cardPurses: buildPayload(),
      });
    }
    reset();
    onCancel();
    onSuccess?.();
  }, [hasChanges, patchCardPurses, buildPayload, reset, onCancel, onSuccess]);

  const handleNext = useCallback(() => {
    if (pursesRequiringLockReason.length > 0) {
      goToConfirm();
      return;
    }
    handleSubmit();
  }, [pursesRequiringLockReason, goToConfirm, handleSubmit]);

  return (
    <ModalWrapper
      testId="manage-account-access"
      visible={visible}
      onSetVisible={handleCancel}
      title={step === ManageAccountAccessStep.Form ? 'Manage Account Access' : 'Confirm Account Lock'}
    >
      {isLoading ? (
        <Box align="center" pad="xl">
          <Preloader />
        </Box>
      ) : step === ManageAccountAccessStep.Form ? (
        <ManageAccountAccessForm
          purses={purses}
          lockedIds={lockedIds}
          editableLockedIds={editableLockedIds}
          employeeUnlockForbiddenIds={employeeUnlockForbiddenIds}
          isAllEmployeeUnlockForbidden={isAllEmployeeUnlockForbidden}
          isLoading={isPending}
          onToggle={toggleLock}
          onSetEmployeeUnlockForbidden={setEmployeeUnlockForbidden}
          onSetAllEmployeeUnlockForbidden={setAllEmployeeUnlockForbidden}
          onCancel={handleCancel}
          onNext={handleNext}
        />
      ) : (
        <ConfirmAccountLock
          purses={pursesRequiringLockReason}
          lockReasons={lockReasons}
          lockReasonOptions={lockReasonOptions}
          isLoading={isPending}
          onSetLockReason={setLockReason}
          onBack={goToForm}
          onConfirm={handleSubmit}
        />
      )}
    </ModalWrapper>
  );
};

export default ManageAccountAccessModal;
